import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import SessionLimitModal from './SessionLimitModal';
import SessionClaimController from './SessionClaimController';

/**
 * Context + Provider for the concurrent-session take-over modal (TD-2239).
 * Mounted once at the app shell. It also mounts SessionClaimController, which
 * drives the login claim + focus/route re-check and opens this modal on a
 * denied login claim.
 *
 * Imperative API via useSessionLimitModal():
 *   - openSessionLimitModal({ oldestEmail, maxConcurrentUsers, onTakeoverSuccess,
 *     onCancel }) — opens the modal. Take over (force claim) → onTakeoverSuccess;
 *     Cancel/Wait → onCancel (the controller passes authStore.logout).
 *   - closeSessionLimitModal()
 *
 * The callbacks are held in a ref so the modal's handlers read the latest values
 * without re-binding every render. Only the login claim opens this modal now —
 * the old per-action agent gate and the at-cap chip are gone.
 */
const SessionLimitModalContext = createContext(null);

export const useSessionLimitModal = () => {
  const ctx = useContext(SessionLimitModalContext);
  if (!ctx) {
    throw new Error('useSessionLimitModal must be used within SessionLimitModalProvider');
  }
  return ctx;
};

const NO_CALLBACKS = { onTakeoverSuccess: null, onCancel: null };

export const SessionLimitModalProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [info, setInfo] = useState({ oldestEmail: null });
  const callbacksRef = useRef(NO_CALLBACKS);

  const openSessionLimitModal = useCallback(
    ({ oldestEmail = null, onTakeoverSuccess = null, onCancel = null } = {}) => {
      callbacksRef.current = { onTakeoverSuccess, onCancel };
      setInfo({ oldestEmail });
      setIsOpen(true);
    },
    []
  );

  const closeSessionLimitModal = useCallback(() => {
    callbacksRef.current = NO_CALLBACKS;
    setIsOpen(false);
  }, []);

  const handleTakeoverSuccess = useCallback(() => {
    const cb = callbacksRef.current.onTakeoverSuccess;
    callbacksRef.current = NO_CALLBACKS;
    setIsOpen(false);
    if (cb) cb();
  }, []);

  const handleCancel = useCallback(() => {
    const cb = callbacksRef.current.onCancel;
    callbacksRef.current = NO_CALLBACKS;
    setIsOpen(false);
    if (cb) cb();
  }, []);

  const value = useMemo(
    () => ({ openSessionLimitModal, closeSessionLimitModal }),
    [openSessionLimitModal, closeSessionLimitModal]
  );

  return (
    <SessionLimitModalContext.Provider value={value}>
      {children}
      <SessionClaimController openModal={openSessionLimitModal} />
      <SessionLimitModal
        isOpen={isOpen}
        oldestEmail={info.oldestEmail}
        onTakeoverSuccess={handleTakeoverSuccess}
        onCancel={handleCancel}
      />
    </SessionLimitModalContext.Provider>
  );
};

SessionLimitModalProvider.propTypes = {
  children: PropTypes.node
};

export default SessionLimitModalProvider;
